import { z } from 'zod'
import { linearFeetSchema } from '../validators/decimal-string.validator.js'
import { moneyAmountSchema } from '../validators/money.validator.js'
import { uuidSchema } from '../validators/uuid.validator.js'
import type { SolutionPricingBasis } from '../enums/solution-pricing-basis.enum.js'
import { seasonSchema } from '../enums/season.enum.js'

/**
 * One basis on which a solution is charged, and the rate for it.
 *
 * Time is the only basis whose rate may be null, and null there means "use
 * the organization's tree labor rate". The other bases require a money
 * string. One object for every basis would let a stored null on the foot,
 * the inch, or the tree through, and a list that failed to parse would drop
 * the treatments with it.
 */
export const solutionPricingDtoSchema = z.discriminatedUnion('basis', [
  z.object({
    basis: z.literal('TIME'),
    /** Dollars per hour. Null means the organization's tree labor rate. */
    unitRate: moneyAmountSchema.nullable(),
  }),
  z.object({
    basis: z.literal('LINEAR_FEET'),
    /** Dollars per foot. */
    unitRate: moneyAmountSchema,
  }),
  z.object({
    basis: z.literal('DBH_INCH'),
    /** Dollars per inch of trunk. */
    unitRate: moneyAmountSchema,
  }),
  z.object({
    basis: z.literal('PER_TREE'),
    /** Dollars for the tree. */
    unitRate: moneyAmountSchema,
  }),
])

export type SolutionPricingDto = z.infer<typeof solutionPricingDtoSchema>

/**
 * A stored rate as the wire sees it.
 *
 * The column is nullable for every basis. Time may stay null. Any other
 * basis with no rate is left out of the list: sending null would not match
 * this schema, and failing the list would hide every treatment on it.
 * Leaving the basis out prices the same as a rate that is not there.
 */
export function toSolutionPricingDto(
  basis: SolutionPricingBasis,
  unitRate: string | null,
): SolutionPricingDto | null {
  switch (basis) {
    case 'TIME':
      return { basis, unitRate }
    case 'LINEAR_FEET':
    case 'DBH_INCH':
    case 'PER_TREE':
      return unitRate === null ? null : { basis, unitRate }
    default: {
      const unhandled: never = basis
      return unhandled
    }
  }
}

/** A treatment offered for one problem type. */
export const problemSolutionDtoSchema = z.object({
  id: uuidSchema,
  problemTypeId: uuidSchema,
  label: z.string(),
  description: z.string().nullable(),
  sortOrder: z.number().int().nonnegative(),
  archivedAt: z.string().datetime().nullable(),
  /**
   * How it is charged. Empty is allowed and means the treatment is recorded
   * but adds nothing to the price — "monitor annually" is real work to note
   * and nothing to bill.
   */
  pricings: z.array(solutionPricingDtoSchema),
  /**
   * The season this treatment is usually done in, which a surveyor choosing
   * it starts from: dormant oil in winter, bark banding in spring. Only a
   * default, so the surveyor can change it on the tree. Null when it has no
   * usual season, and then it starts with the tree's pruning.
   */
  usualSeason: seasonSchema.nullable().default(null),
})

export type ProblemSolutionDto = z.infer<typeof problemSolutionDtoSchema>

/**
 * A solution chosen for one problem on one tree, with its quantities and the
 * money that falls out of them.
 *
 * `amount` is computed by the API rather than the browser: it depends on the
 * organization's labor rate and on the tree's diameter, and a total assembled
 * in two places is a total that will eventually disagree with itself.
 */
export const treeProblemSolutionDtoSchema = z.object({
  id: uuidSchema,
  solutionId: uuidSchema,
  /** Denormalised for display: the picker is not always loaded beside it. */
  label: z.string(),
  minutes: z.number().int().nonnegative().nullable(),
  /** Feet, `Decimal(8, 2)`. The converter sends `toFixed(2)`. */
  linearFeet: linearFeetSchema.nullable(),
  /** Exact decimal string, or null when nothing here can be priced yet. */
  amount: moneyAmountSchema.nullable(),
  /**
   * When this treatment should be done on this tree. Null when no season was
   * chosen, which a proposal reads as "with the next visit". Defaulted for a
   * tree a phone cached before seasons existed.
   */
  season: seasonSchema.nullable().default(null),
})
